import { WfnMultiSelect } from '@adp-wfn/mdf-wc-react';
import { SdfCheckbox, SdfCheckboxGroup } from '@waypoint/react-components';
import React, { useCallback, useMemo } from 'react';
import { BaseField } from './BaseField';
import { useFieldLogic } from './hooks';
import { Field } from './types';

/**
 * Multi-select field component (checkboxes or multi-select dropdown)
 * @param props - Field component props
 * @param props.data - Field configuration with options and values array
 * @param props.onChange - Callback function for selection changes
 * @param props.isHorizontal - Whether to render checkboxes horizontally
 * @param props.renderPreference - Rendering preference (compact/expanded/auto)
 * @returns JSX element for multi-select field
 */
export function SomeOf({ data, onChange, isHorizontal, renderPreference = 'auto' }: Field) {
  const { useCompactControl } = useFieldLogic(data.options, renderPreference, 4);

  const dropdownData = useMemo(
    () => data.options?.map((item) => ({ label: item.label || item.text, value: item.value, disabled: false })) || [],
    [data.options]
  );

  const handleChange = useCallback(
    (event: any) => onChange(event?.detail?.map((d) => d.value) || []),
    [onChange]
  );

  const handleCheckboxChange = useCallback(
    (event: any) => onChange(event.detail),
    [onChange]
  );

  if (useCompactControl) {
    return (
      <BaseField>
        <WfnMultiSelect
          label={data.label}
          data={dropdownData}
          value={data.value}
          ref={(el) => {
            if (el) {
              el.addEventListener('sdfChange', (event: any) => {
                handleChange(event);
              });
            }
          }}
        />
      </BaseField>
    );
  }

  return (
    <BaseField>
      <SdfCheckboxGroup
        label={data.label}
        direction={isHorizontal ? 'row' : 'column'}
        value={data.value || []}
        onSdfChange={handleCheckboxChange}
      >
        {data.options?.map((item) => (
          <SdfCheckbox
            key={item.id}
            value={item.value}
            label={item.label || item.text}
          />
        ))}
      </SdfCheckboxGroup>
    </BaseField>
  );
}
